<template>
  <div class="widget w-world-clock-widget">
    <div class="widget-body" @mouseover="clockHoverActive = true" @mouseleave="clockHoverActive = false">
      <div class="header-bar">
        <div class="display-view">
          <c-widget-header-dropdown right dropleft>
            <template slot="content">
              <div class="clock-type d-flex align-items-center justify-content-between p-2">
                <span>Clock Type</span>
                <v-form-radio-group
                  name="clockTypeSelect"
                  buttons
                  button-variant="outline-primary"
                  :checked="clockType"
                  :options="[{ value: 'digital', text: 'Digital' }, { value: 'analog', text: 'Analog' }]"
                  @input="onUpdateClockType"
                >
                </v-form-radio-group>
              </div>
              <v-dropdown-group v-for="marketZone in marketZones" :key="marketZone.zone">
                <v-dropdown-header class="pl-2 pt-1 pb-1">
                  {{ $t(`${i18nNamespace}.zones.${marketZone.zone}`) }}
                </v-dropdown-header>
                <v-dropdown-item
                  v-for="zoneItem in marketZone.zoneItems"
                  :key="zoneItem.name"
                  @click="onMarketZoneSelect(zoneItem)"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <span class="indicator ml-2 mr-2" :class="zoneItem.state"></span>
                      <span class="zone-ric"> {{ zoneItem.name }} </span>
                    </div>
                    <span class="zone-place"> {{ $t(`${i18nNamespace}.places.${zoneItem.place}`) }} </span>
                  </div>
                </v-dropdown-item>
              </v-dropdown-group>
            </template>
          </c-widget-header-dropdown>
          {{ $t(`${i18nNamespace}.places.${clockData.place}`) }}
        </div>
        <div class="actions-view d-flex align-items-center justify-content-center">
          <transition name="fade" mode="out-in">
            <span v-if="clockHoverActive" @click="$emit('close')">
              <font-awesome-icon :icon="['fas', 'times']" />
            </span>
          </transition>
        </div>
      </div>
      <c-streaming-error :show="hasStreamingError" />
      <div class="clock-area d-flex justify-content-center">
        <component v-bind:is="clock()" :timeZone="clockData.timeZone" @timer="onRealtime"></component>
      </div>

      <transition name="fade">
        <v-alert show v-if="hasError" variant="red" align="center">{{ i18n('error', 'globalMessages') }}</v-alert>
        <div v-else class="d-flex justify-content-center">
          <span class="baseline">{{ clockData.name }}</span>
          <c-trend-arrow
            :class="`ml-2 ${getChangeClass(quoteData['q._PCTCHNG'])}`"
            :orientation="getTrend(quoteData['q._PCTCHNG'])"
          />
          <c-tick-flash :class="`ml-1 ${getChangeClass(quoteData['q._PCTCHNG'])}`" :value="quoteData['q._PCTCHNG']">
            {{ $f(quoteData['q._PCTCHNG'], 'float.percentage.change') }}
          </c-tick-flash>
        </div>
      </transition>

      <div class="status-bar text-center">
        <transition name="fade">
          <div v-if="clockData.remaining" :class="clockData.state">
            <span>
              {{ $t(`${i18nNamespace}.${clockData.state}.state`) }}
            </span>
            {{ $t(`${i18nNamespace}.${clockData.state}.label`) }}
            {{ clockData.remaining }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import availableMarketZones from '@/utils/marketZones';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWorldClockAnalog from '@custom/CWorldClockAnalog';
import CWorldClockDigital from '@custom/CWorldClockDigital';
import CTrendArrow from '@custom/CTrendArrow';
import CWidgetHeaderDropdown from '@custom/widgetContainer/CWidgetHeaderDropdown';
import CTickFlash from '@custom/CTickFlash';
import CStreamingError from '@custom/CStreamingError';
import widgetCommons from '@mixins/widgetCommons';
import { API } from '@state/api';
import { toTimezoneDate } from '@/utils/date';

import moment from 'moment';
import { capitalize } from 'lodash-es';

export default {
  name: 'w-world-clock-widget',
  components: {
    CWidgetHeader,
    CWidgetHeaderButton,
    CWorldClockAnalog,
    CTrendArrow,
    CWidgetHeaderDropdown,
    CWorldClockDigital,
    CTickFlash,
    CStreamingError
  },
  mixins: [widgetCommons],
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      hasError: false,
      hasStreamingError: false,
      isReconnect: false,
      subscriptionId: null,
      clockHoverActive: false,
      quoteData: {},
      clockData: {},
      marketZones: [...availableMarketZones]
    };
  },
  computed: {
    ric() {
      return this.$store.state.widget[this.wid].ric;
    },
    clockType() {
      return this.$store.state.widget[this.wid].clockType;
    },
    i18nNamespace() {
      return 'white-label.widgets.worldClock';
    }
  },
  methods: {
    clock() {
      return `CWorldClock${capitalize(this.clockType)}`;
    },
    onUpdateClockType(clockType) {
      this.$store.commit(`widget/${this.wid}/CLOCK_TYPE_CHANGED`, clockType);
    },
    onMarketZoneSelect(clockData) {
      this.clockData = Object.assign(
        {
          remaining: null
        },
        clockData
      );

      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, clockData.ric);

      this.quoteData = {};
      this.fetch();
    },
    onRealtime({ date }) {
      const marketDates = this.getMarketDates(date, this.clockData.open, this.clockData.close);
      const { remaining, state } = this.getMarketState(marketDates);

      this.clockData.state = state;
      this.clockData.remaining = remaining;
    },
    getMarketDates(date, opensAt, closesAt) {
      const localeDateTime = moment(date, 'MM/DD/YYYY HH:mm:ss');

      const [oH, oM, oS] = opensAt.split(':');
      const openDate = new Date(date);
      openDate.setHours(oH, oM, oS);

      const [cH, cM, cS] = closesAt.split(':');
      const closeDate = new Date(date);
      closeDate.setHours(cH, cM, cS);

      return {
        localeDateTime,
        openDate,
        closeDate
      };
    },
    getMarketState({ localeDateTime, openDate, closeDate }) {
      let state = 'closed';
      let remaining = null;
      if (localeDateTime.isAfter(openDate) && localeDateTime.isBefore(closeDate)) {
        // is open
        state = 'open';
        remaining = moment.utc(moment(closeDate).diff(localeDateTime));
      } else if (localeDateTime.isBefore(openDate)) {
        // opens later that day
        remaining = moment.utc(moment(openDate).diff(localeDateTime));
      } else {
        // opens the next day
        remaining = moment.utc(
          moment(openDate)
            .add(1, 'days')
            .diff(localeDateTime)
        );
      }

      remaining = moment(remaining).format('HH:mm:ss');

      return {
        state,
        remaining
      };
    },
    calculateAllMarketStates() {
      this.marketZones.map(zone => {
        zone.zoneItems.forEach(item => {
          let { open, close, timeZone } = item;
          const marketDates = this.getMarketDates(toTimezoneDate(timeZone), open, close);
          const { state } = this.getMarketState(marketDates);
          item.state = state;
        });
      });
    },
    fetch() {
      this.streamingUnsubscribe();
      this.hasError = false;
      this.isReconnect = false;
      this.streamingSubscribe();
    },
    // Streaming
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingSubscribe() {
      this.streamingConnect().then(() => {
        this.subscriptionId = API.Streaming.Quote.subscribe(
          {
            rics: [this.ric],
            fids: ['q._PCTCHNG']
          },
          // subscribe
          this.handleSubscription,
          // snapshot
          this.handleSnapshot,
          // update
          this.handleUpdate
        );
      });
    },
    handleSubscription(_res, error) {
      if (error) {
        if (this.isReconnect) this.hasStreamingError = true;
        else this.hasError = true;
        console.warn('Streaming subscription error', error);
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        if (this.isReconnect) this.hasStreamingError = true;
        else this.hasError = true;
        console.warn('Streaming snapshot error', error);
      } else {
        this.quoteData = response.payload[0].data;
        this.isReconnect = true;
        this.hasStreamingError = false;
      }
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
      } else {
        Object.assign(this.quoteData, response.payload[0].data);
        this.hasStreamingError = false;
      }
    },
    streamingUnsubscribe() {
      this.streamingConnect().then(() => {
        if (this.subscriptionId) {
          API.Streaming.Quote.unsubscribe(this.subscriptionId);
        }
      });
    },
    getTrend(value) {
      return value > 0 ? 'up' : value < 0 ? 'down' : 'right';
    },
    getChangeClass(value) {
      return value > 0 ? 'positive-change' : value < 0 ? 'negative-change' : 'neutral-change';
    },
    getClockData(ric) {
      let zoneItem = {
        remaining: null
      };
      this.marketZones.forEach(m => {
        m.zoneItems.forEach(item => {
          if (item.ric === ric) {
            zoneItem = {
              ...zoneItem,
              ...item
            };
          }
        });
      });
      return zoneItem;
    }
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    this.$root.$off('bv::dropdown::show');
  },
  beforeMount() {
    this.clockData = this.getClockData(this.ric);
  },
  mounted() {
    this.$root.$on('bv::dropdown::show', () => {
      this.calculateAllMarketStates();
    });
    this.fetch();
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/world-clock-widget';
</style>
